import * as path from 'node:path';
import { getBridgeRoot } from '../../files/cwd/index.js';
import { forEachWithGitYield } from '../../git/git-runtime.js';
import type { LogFn } from '../../log.js';
import { discoverPreviewWorktreeForBridge } from '../discovery/discover-preview-worktree-on-disk.js';
import { prepareNewIsolatedWorktrees } from '../prepare/index.js';
import { removeSessionWorktrees } from '../remove-session-worktrees.js';
import type { WorktreeLayoutFile } from '../worktree-layout-file.js';
import { loadWorktreeLayout } from '../worktree-layout-file.js';
import type { PreviewWorktreeCache } from './preview-worktree-cache.js';
import { PreviewWorktreeCache as PreviewWorktreeCacheClass } from './preview-worktree-cache.js';

export type PreviewWorktreeManagerOptions = {
  worktreesRootPath: string;
  log: LogFn;
};

export class PreviewWorktreeManager {
  readonly worktreesRootPath: string;
  private readonly log: LogFn;
  private readonly cache: PreviewWorktreeCache;
  private readonly layout: WorktreeLayoutFile;

  constructor(options: PreviewWorktreeManagerOptions) {
    this.worktreesRootPath = options.worktreesRootPath;
    this.log = options.log;
    this.cache = new PreviewWorktreeCacheClass();
    this.layout = loadWorktreeLayout();
  }

  getPreviewParentPath(environmentId: string): string | undefined {
    return this.cache.getParentPath(environmentId.trim());
  }

  async getPreviewParentPathAsync(environmentId: string): Promise<string | undefined> {
    const eid = environmentId.trim();
    const cached = this.cache.getParentPath(eid);
    if (cached) return cached;
    if (await this.ensureCached(eid)) {
      return this.cache.getParentPath(eid);
    }
    return undefined;
  }

  getRepoCheckoutPaths(environmentId: string): string[] | undefined {
    return this.cache.getRepoCheckoutPaths(environmentId.trim());
  }

  async ensureCached(environmentId: string): Promise<boolean> {
    const eid = environmentId.trim();
    if (this.cache.hasEnvironment(eid)) return true;
    const discovered = await discoverPreviewWorktreeForBridge({
      environmentId: eid,
      worktreesRootPath: this.worktreesRootPath,
      layout: this.layout,
    });
    if (!discovered) return false;
    this.cache.remember(eid, discovered);
    return true;
  }

  async createWorktrees(environmentId: string, branch: string): Promise<string | undefined> {
    const eid = environmentId.trim();
    if (this.cache.hasEnvironment(eid)) {
      return this.cache.getParentPath(eid);
    }
    await this.ensureCached(eid);
    if (this.cache.hasEnvironment(eid)) {
      return this.cache.getParentPath(eid);
    }

    const binding = await prepareNewIsolatedWorktrees({
      kind: 'preview_environment',
      scopeId: eid,
      branch,
      worktreesRootPath: this.worktreesRootPath,
      bridgeRoot: getBridgeRoot(),
      layout: this.layout,
      log: this.log,
      existingBranch: branch,
    });
    if (!binding) return undefined;
    this.cache.remember(eid, binding);
    return binding.parentPath;
  }

  async removePreviewWorktrees(environmentId: string): Promise<void> {
    const paths = this.cache.clearEnvironment(environmentId.trim());
    if (!paths?.length) return;
    await removeSessionWorktrees(paths, this.worktreesRootPath, this.log);
  }

  /** Drop worktrees for preview environments no longer present in bridge config. */
  async syncConfiguredEnvironments(environmentIds: string[]): Promise<void> {
    const configured = new Set(environmentIds.map((id) => id.trim()).filter(Boolean));
    await forEachWithGitYield(this.cache.listEnvironmentIds(), async (existingId) => {
      if (!configured.has(existingId)) {
        await this.removePreviewWorktrees(existingId);
      }
    });
  }
}
